import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import FileUpload from "../../components/FileUpload";
import CTextField from "../../components/CTextField.js";
import CButton from "../../components/CButton.js";
import CDataGrid from "../../components/CDataGrid.js";
import ChatModule from "./ChatModule.js";
import { ReactComponent as FileIcon } from "../../assets/images/file-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import { supabase } from "../../config/supabaseClient";
import { toast } from "react-toastify";
import {
  getLocalData,
  archiveRecords,
} from "../../constant/utils/commonUtils.js";
import CDialog from "../../components/CDialog";
import { useDispatch, useSelector } from "react-redux";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import CFormProvider from "../../components/CFormProvider";
import CFormTextField from "../../components/HookFormComponents/CFormTextField";
import { CFormSelect } from "../../components/HookFormComponents/CFormSelect";

import {
  addGoal,
  fetchGoals,
  archiveGoal,
  updateGoal,
  addCriticalSkill,
  fetchCriticalSkills,
  updateCriticalSkill,
  archiveCriticalSkill,
  parseAndSaveCriticalSkills,
} from "../../slices/trainingStrategyInputSlice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ReactComponent as EditIcon } from "../../assets/images/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import { ReactComponent as FileEdit } from "../../assets/images/file-edit-icon.svg";
import { executeChatPrompt } from "../../slices/trainingStrategyInputSlice";
import { CHAT_CATEGORIES } from "../../constant/constants";
import { DEFAULT_PROMPTS } from "../../constant/prompt";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as SaveIcon } from "../../assets/images/save-icon.svg";
import { ReactComponent as ExportIcon } from "../../assets/images/upload-icon.svg";
import CSelect from "../../components/CSelect";

function StrategicInputForm() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [fileName, setFileName] = useState("");
  const [parsedText, setParsedText] = useState("");
  const [currentFilePath, setCurrentFilePath] = useState("");
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [industryTrendsResponse, setIndustryTrendsResponse] = useState("");
  const [workforceImpactResponse, setWorkforceImpactResponse] = useState("");
  const [trainingResponse, setTrainingResponse] = useState("");
  const [strategicSummaryResponse, setStrategicSummaryResponse] = useState("");
  const [showAddGoalsDialog, setShowAddGoalsDialog] = useState(false);
  const [dialogState, setDialogState] = useState({
    show: false,
    type: "",
  });
  const [editingGoal, setEditingGoal] = useState(null);
  const [deleteDialogState, setDeleteDialogState] = useState({
    id: null,
    show: false,
  });
  
  // Form for Goals
  const goalMethods = useForm({
    defaultValues: {
      sr_number: "",
      goal: "",
      benefits: "",
      timeline: "",
    },
  });
  
  const [showAddCriticalSkillDialog, setShowAddCriticalSkillDialog] =
    useState(false);
  const [editingCriticalSkill, setEditingCriticalSkill] = useState(null);
  
  // Form for Critical Skills
  const criticalSkillMethods = useForm({
    defaultValues: {
      skill_name: "",
      description: "",
      level: "",
      importance: "",
    },
  });
  const [deleteCriticalSkillDialogState, setDeleteCriticalSkillDialogState] =
    useState({
      id: null,
      show: false,
    });
  const [isSavingStrategicInput, setIsSavingStrategicInput] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isSavingGoal, setIsSavingGoal] = useState(false);
  const [isSavingCriticalSkill, setIsSavingCriticalSkill] = useState(false);
  const [chatHistories, setChatHistories] = useState({
    industryTrends: [],
    workforceImpact: [],
    training: [],
    strategicSummary: [],
    criticalSkills: [],
  });
  const [prompts, setPrompts] = useState({});

  // Constants for field limits
  const SKILL_NAME_MIN_LENGTH = 10;
  const SKILL_NAME_MAX_LENGTH = 100;
  const DESCRIPTION_MIN_LENGTH = 20;
  const DESCRIPTION_MAX_LENGTH = 500;

  const dispatch = useDispatch();
  const { goals, goalsLoading, criticalSkills, criticalSkillsLoading } =
    useSelector((state) => state.trainingStrategyInput);

  // Fetch latest file data on component mount
  useEffect(() => {
    const fetchLatestFile = async () => {
      try {
        // Check if file has been removed by user (only affects file UI, not data)
        const storageKey = `strategicInputForm_file_removed`;
        const isFileRemoved = localStorage.getItem(storageKey) === "true";

        // Always fetch data from database (data should persist even after file removal)
        const { data: latestFile, error } = await supabase
          .from("strategic_input")
          .select("*")
          .eq("company_id", getLocalData()?.company_id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error("Error fetching latest file:", error);
          return;
        }

        if (latestFile) {
          // Always restore all data fields from the database
          setParsedText(latestFile.extracted_text || "");
          setIndustryTrendsResponse(latestFile.industry_trends || "");
          setWorkforceImpactResponse(
            latestFile.impact_on_workforce_and_skills_development || ""
          );
          setTrainingResponse(
            latestFile.training_and_development_response || ""
          );
          setStrategicSummaryResponse(latestFile.strategic_input_summary || "");

          // Only restore file-related UI if file hasn't been removed
          if (!isFileRemoved && latestFile.file_url) {
            setCurrentFilePath(latestFile.file_url);
            setFileName(latestFile.file_url.split("_").pop());
            setUploaded(true);
          }
        }
      } catch (error) {
        console.error("Error in fetchLatestFile:", error);
      }
    };

    fetchLatestFile();
  }, []);

  useEffect(() => {
    dispatch(fetchGoals());
    dispatch(fetchCriticalSkills());
  }, [dispatch]);

  // Add function to fetch prompts by category
  const fetchPromptsByCategory = async (category) => {
    try {
      const { data, error } = await supabase
        .from("prompt_master")
        .select("*")
        .eq("category", category)
        .eq("status", "true")
        .order("index", { ascending: true });

      if (error) {
        throw error;
      }

      // If no prompts found in database, use default prompts
      if (!data || data.length === 0) {
        const defaultPrompt = DEFAULT_PROMPTS.find(
          (p) => p.category === category
        );
        if (defaultPrompt) {
          return [defaultPrompt];
        }
      }

      return data;
    } catch (error) {
      console.error(`Error fetching prompts for ${category}:`, error);
      toast.error(`Failed to fetch prompts for ${category}`);
      return [];
    }
  };

  // Modify toggleChat to fetch prompts when opening chat
  const toggleChat = async (section) => {
    setActiveSection(section);
    const newIsOpen = !isChatOpen;
    setIsChatOpen(newIsOpen);

    if (newIsOpen) {
      const categoryPrompts = await fetchPromptsByCategory(section);
      setPrompts((prev) => ({
        ...prev,
        [section]: categoryPrompts,
      }));
    }
  };

  const handleAIResponse = (response, promptType, updatedMessages) => {
    setChatHistories((prev) => ({
      ...prev,
      [promptType]: updatedMessages || prev[promptType],
    }));

    // First, add the user's message to the chat
    const userMessage = {
      role: "user",
      content: `Please analyze the following ${promptType}:\n${
        parsedText || ""
      }`,
    };

    // Then add the AI response
    const aiMessage = {
      role: "assistant",
      content: response,
    };

    // Update the state based on the prompt type
    switch (promptType) {
      case "industryTrends":
        setIndustryTrendsResponse(response);
        break;
      case "workforceImpact":
        setWorkforceImpactResponse(response);
        break;
      case "training":
        setTrainingResponse(response);
        break;
      case "strategicSummary":
        setStrategicSummaryResponse(response);
        break;
      default:
        console.warn("Unknown prompt type:", promptType);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File size check (5 MB)
    const MAX_FILE_SIZE_MB = 5;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error("File size should not exceed 5 MB");
      return;
    }

    const allowedExtensions = ["docx"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error("Please upload only docx files");
      return;
    }

    setUploading(true);
    setFileName(file.name);

    // Reset the removed state when uploading a new file
    const storageKey = `strategicInputForm_file_removed`;
    localStorage.removeItem(storageKey);

    const filePath = `files/strategic_inputs/${Date.now()}_${
      getLocalData()?.company_id
    }_${file.name}`;

    try {
      // Upload file to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("files")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        if (uploadError.message.includes("DatabaseError")) {
          toast.error("Database configuration error. Please contact support.");
        } else {
          toast.error("Failed to upload file. Please try again.");
        }
        setUploading(false);
        setUploaded(false);
        setFileName("");
        return;
      }

      // Set the file path for subscription
      setCurrentFilePath(filePath);

      // Show success toast with file name
      toast.success(`File "${file.name}" uploaded successfully!`);
      setUploaded(true);
      setUploading(false);
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error(error.message || "Upload failed. Please try again.");
      setUploading(false);
      setUploaded(false);
      setFileName("");
    }
  };

  useEffect(() => {
    if (!currentFilePath) return;

    const strategicInputSubTable = supabase
      .channel("strategicInputSubTable")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "strategic_input",
          filter: `file_url=eq.${currentFilePath}`,
        },
        (payload) => {
          if (payload.new && payload.new.extracted_text) {
            setParsedText(payload.new.extracted_text);
          }
        }
      )
      .subscribe(() => {});

    return () => {
      strategicInputSubTable.unsubscribe();
    };
  }, [currentFilePath]);

  const clearFile = async () => {
    // Store the file path before clearing state
    const filePathToDelete = currentFilePath;
    
    // Clear file-related UI state immediately (keep all other data intact)
    setFileName("");
    setUploaded(false);
    setUploading(false);
    setCurrentFilePath("");

    // Set removed state in localStorage to prevent showing file UI on refresh
    const storageKey = `strategicInputForm_file_removed`;
    localStorage.setItem(storageKey, "true");

    const fileInput = document.getElementById("fileInput");
    if (fileInput) {
      fileInput.value = "";
    }

    // Delete file from storage and update database record
    if (filePathToDelete) {
      try {
        // Delete file from storage
        const { error: storageError } = await supabase.storage
          .from("files")
          .remove([filePathToDelete]);

        if (storageError) {
          console.error("Error deleting file from storage:", storageError);
          toast.error("Failed to delete file from storage. Please try again.");
          return;
        }

        // Update database record to clear file_url (keep all other data)
        const { data: record } = await supabase
          .from("strategic_input")
          .select("id")
          .eq("file_url", filePathToDelete)
          .eq("company_id", getLocalData()?.company_id)
          .single();

        if (record) {
          const { error: updateError } = await supabase
            .from("strategic_input")
            .update({ file_url: null })
            .eq("id", record.id);

          if (updateError) {
            console.error("Error updating database record:", updateError);
            // Don't show error toast for DB update failure, file is already deleted from storage
          }
        }

        toast.success("File removed successfully");
      } catch (error) {
        console.error("Error in clearFile:", error);
        toast.error("An error occurred while removing the file. Please try again.");
      }
    }
  };

  const handleCloseDialog = () => {
    setShowContentDialog(false);
  };

  const handleAddGoal = () => {
    // Auto-generate sr_number for new goal (reverse order: newest gets 1)
    const nextSrNumber = 1; // New goal always gets number 1
    goalMethods.reset({
      sr_number: nextSrNumber.toString(),
      goal: "",
      benefits: "",
      timeline: "",
    });
    setEditingGoal(null);
    setShowAddGoalsDialog(true);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    goalMethods.reset({
      sr_number: goal.sr_number,
      goal: goal.goal,
      benefits: goal.benefits,
      timeline: goal.timeline,
    });
    setShowAddGoalsDialog(true);
  };

  const handleArchiveGoal = async (goalId) => {
    setDeleteDialogState({ id: goalId, show: true });
  };

  const handleConfirmArchive = async () => {
    try {
      // Find the goal being archived to get its sr_number
      const goalToArchive = goals.find(
        (goal) => goal.id === deleteDialogState.id
      );
      const archivedSrNumber = goalToArchive ? goalToArchive.sr_number : 0;

      const result = await archiveRecords(
        supabase,
        "strategic_input_goals",
        "id",
        deleteDialogState.id
      );

      if (result.success) {
        // Decrement sr_number for all goals that had a higher number than the archived goal
        const goalsToUpdate = goals.filter(
          (goal) =>
            goal.id !== deleteDialogState.id &&
            goal.sr_number > archivedSrNumber
        );

        if (goalsToUpdate.length > 0) {
          const updatePromises = goalsToUpdate.map((goal) =>
            dispatch(
              updateGoal({
                id: goal.id,
                sr_number: goal.sr_number - 1,
                goal: goal.goal,
                benefits: goal.benefits,
                timeline: goal.timeline,
              })
            ).unwrap()
          );

          // Wait for all updates to complete
          await Promise.all(updatePromises);
        }

        toast.success("Goal deleted successfully!");
        // Refresh the goals list to update the table
        dispatch(fetchGoals());
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error archiving goal:", error);
      toast.error("Failed to archive goal: " + error.message);
    }
    setDeleteDialogState({ id: null, show: false });
  };

  const handleSubmitGoal = async (data) => {
    // Prevent multiple submissions
    if (isSavingGoal) {
      return;
    }

    // Validate required fields (sr_number is auto-generated)
    if (!data.goal || !data.benefits || !data.timeline) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSavingGoal(true);
    try {
      const goalData = {
        sr_number: parseInt(data.sr_number, 10),
        goal: data.goal.trim(),
        benefits: data.benefits.trim(),
        timeline: data.timeline.trim(),
      };

      if (editingGoal) {
        goalData.id = editingGoal.id;
        await dispatch(updateGoal(goalData)).unwrap();
        toast.success("Goal updated successfully!");
      } else {
        // For new goals, we need to increment all existing goals' sr_number by 1
        // since the new goal will get sr_number = 1
        if (goals.length > 0) {
          // Update all existing goals to increment their sr_number
          const updatePromises = goals.map((goal) =>
            dispatch(
              updateGoal({
                id: goal.id,
                sr_number: goal.sr_number + 1,
                goal: goal.goal,
                benefits: goal.benefits,
                timeline: goal.timeline,
              })
            ).unwrap()
          );

          // Wait for all updates to complete
          await Promise.all(updatePromises);
        }

        await dispatch(addGoal(goalData)).unwrap();
        toast.success("Goal added successfully!");
      }

      handleCloseAddGoalsDialog();
      dispatch(fetchGoals());
    } catch (error) {
      console.error("Error submitting goal:", error);
      toast.error(
        `Failed to ${editingGoal ? "update" : "add"} goal: ` + error.message
      );
    } finally {
      setIsSavingGoal(false);
    }
  };

  const handleCloseAddGoalsDialog = () => {
    setShowAddGoalsDialog(false);
    setEditingGoal(null);
    goalMethods.reset({
      sr_number: "",
      goal: "",
      benefits: "",
      timeline: "",
    });
  };

  // Critical Skills handlers
  const handleAddCriticalSkill = () => {
    criticalSkillMethods.reset({
      skill_name: "",
      description: "",
      level: "",
      importance: "",
    });
    setEditingCriticalSkill(null);
    setShowAddCriticalSkillDialog(true);
  };

  const handleEditCriticalSkill = (skill) => {
    setEditingCriticalSkill(skill);
    criticalSkillMethods.reset({
      skill_name: skill.skill_name,
      description: skill.description || "",
      level: skill.level,
      importance: skill.importance,
    });
    setShowAddCriticalSkillDialog(true);
  };

  const handleArchiveCriticalSkill = (skillId) => {
    setDeleteCriticalSkillDialogState({ id: skillId, show: true });
  };

  const handleConfirmArchiveCriticalSkill = async () => {
    try {
      await dispatch(
        archiveCriticalSkill(deleteCriticalSkillDialogState.id)
      ).unwrap();
      toast.success("Critical skill deleted successfully!");
      dispatch(fetchCriticalSkills());
    } catch (error) {
      console.error("Error archiving critical skill:", error);
      toast.error("Failed to delete critical skill: " + error.message);
    }
    setDeleteCriticalSkillDialogState({ id: null, show: false });
  };

  const handleSubmitCriticalSkill = async (data) => {
    // Prevent multiple submissions
    if (isSavingCriticalSkill) {
      return;
    }

    // Validate required fields
    if (!data.skill_name || !data.description || !data.level || !data.importance) {
      toast.error("Please fill in all fields");
      return;
    }

    // Validate skill name length
    const trimmedSkillName = data.skill_name.trim();
    if (trimmedSkillName.length < SKILL_NAME_MIN_LENGTH) {
      criticalSkillMethods.setError("skill_name", {
        type: "manual",
        message: `Skill name must be at least ${SKILL_NAME_MIN_LENGTH} characters long`,
      });
      toast.error(`Skill name must be at least ${SKILL_NAME_MIN_LENGTH} characters long`);
      return;
    }
    if (trimmedSkillName.length > SKILL_NAME_MAX_LENGTH) {
      criticalSkillMethods.setError("skill_name", {
        type: "manual",
        message: `Skill name must not exceed ${SKILL_NAME_MAX_LENGTH} characters`,
      });
      toast.error(`Skill name must not exceed ${SKILL_NAME_MAX_LENGTH} characters`);
      return;
    }

    // Validate description length
    const trimmedDescription = data.description.trim();
    if (trimmedDescription.length < DESCRIPTION_MIN_LENGTH) {
      criticalSkillMethods.setError("description", {
        type: "manual",
        message: `Description must be at least ${DESCRIPTION_MIN_LENGTH} characters long`,
      });
      toast.error(`Description must be at least ${DESCRIPTION_MIN_LENGTH} characters long`);
      return;
    }
    if (trimmedDescription.length > DESCRIPTION_MAX_LENGTH) {
      criticalSkillMethods.setError("description", {
        type: "manual",
        message: `Description must not exceed ${DESCRIPTION_MAX_LENGTH} characters`,
      });
      toast.error(`Description must not exceed ${DESCRIPTION_MAX_LENGTH} characters`);
      return;
    }

    // Validate unique skill name (case-insensitive)
    const existingSkill = criticalSkills.find(
      (skill) =>
        skill.skill_name.toLowerCase() === trimmedSkillName.toLowerCase() &&
        (!editingCriticalSkill || skill.id !== editingCriticalSkill.id)
    );

    if (existingSkill) {
      criticalSkillMethods.setError("skill_name", {
        type: "manual",
        message: "Skill name already exists. Please use a different name.",
      });
      toast.error("Skill name already exists. Please use a different name.");
      return;
    }

    setIsSavingCriticalSkill(true);
    try {
      const skillData = {
        skill_name: trimmedSkillName,
        description: trimmedDescription,
        level: data.level,
        importance: data.importance,
      };

      if (editingCriticalSkill) {
        skillData.id = editingCriticalSkill.id;
        await dispatch(updateCriticalSkill(skillData)).unwrap();
        toast.success("Critical skill updated successfully!");
      } else {
        await dispatch(addCriticalSkill(skillData)).unwrap();
        toast.success("Critical skill added successfully!");
      }

      handleCloseAddCriticalSkillDialog();
      dispatch(fetchCriticalSkills());
    } catch (error) {
      console.error("Error submitting critical skill:", error);
      toast.error(
        `Failed to ${
          editingCriticalSkill ? "update" : "add"
        } critical skill: ` + error.message
      );
    } finally {
      setIsSavingCriticalSkill(false);
    }
  };

  const handleCloseAddCriticalSkillDialog = () => {
    setShowAddCriticalSkillDialog(false);
    setEditingCriticalSkill(null);
    criticalSkillMethods.reset({
      skill_name: "",
      description: "",
      level: "",
      importance: "",
    });
  };

  const columns = [
    {
      field: "sr_number",
      headerName: "Sr. No",
      flex: 1,
    },
    {
      field: "goal",
      headerName: "Goal",
      flex: 2,
    },
    {
      field: "benefits",
      headerName: "Benefits",
      flex: 2,
    },
    {
      field: "timeline",
      headerName: "Timeline",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-center items-center gap-2 w-full h-full">
          <CButton
            tooltip="Edit"
            tooltipPosition={"top"}
            onClick={() => handleEditGoal(params.row)}
            className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <EditIcon className="!fill-blue-500 !text-blue-500" />
          </CButton>
          <CButton
            tooltip="Delete"
            tooltipPosition={"top"}
            onClick={() => handleArchiveGoal(params.row.id)}
            className="!min-w-0 bg-red-100 text-blue-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <DeleteIcon className="!fill-red-500 !text-red-500" />
          </CButton>
        </div>
      ),
    },
  ];

  const criticalSkillsColumns = [
    {
      field: "sr_number",
      headerName: "Sr. No",
      flex: 1,
      renderCell: (params) => {
        // Calculate sr_number based on index (ascending order: 1, 2, 3...)
        const index = criticalSkills.findIndex(
          (skill) => skill.id === params.row.id
        );
        return index + 1;
      },
    },
    {
      field: "skill_name",
      headerName: "Skill Name",
      flex: 2,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 3,
    },
    {
      field: "level",
      headerName: "Level",
      flex: 1,
      renderCell: (params) => {
        const levelLabels = {
          essential: "Essential",
          intermediate: "Intermediate",
          expert: "Expert",
        };
        return levelLabels[params.row.level] || params.row.level;
      },
    },
    {
      field: "importance",
      headerName: "Importance",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-center items-center gap-2 w-full h-full">
          <CButton
            tooltip="Edit"
            tooltipPosition={"top"}
            onClick={() => handleEditCriticalSkill(params.row)}
            className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <EditIcon className="!fill-blue-500 !text-blue-500" />
          </CButton>
          <CButton
            tooltip="Delete"
            tooltipPosition={"top"}
            onClick={() => handleArchiveCriticalSkill(params.row.id)}
            className="!min-w-0 bg-red-100 text-blue-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <DeleteIcon className="!fill-red-500 !text-red-500" />
          </CButton>
        </div>
      ),
    },
  ];

  // Add function to save all fields
  const saveAllFields = async () => {
    try {
      const { data: record } = await supabase
        .from("strategic_input")
        .select("id")
        .eq("company_id", getLocalData()?.company_id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (record) {
        const updateData = {
          extracted_text: parsedText,
          industry_trends: industryTrendsResponse,
          impact_on_workforce_and_skills_development: workforceImpactResponse,
          training_and_development_response: trainingResponse,
          strategic_input_summary: strategicSummaryResponse,
        };

        const { error } = await supabase
          .from("strategic_input")
          .update(updateData)
          .eq("id", record.id);

        if (error) {
          console.error("Error updating fields:", error);
          return { success: false, error: error.message };
        } else {
          return { success: true };
        }
      }
      return { success: false, error: "No record found" };
    } catch (error) {
      console.error("Error saving fields:", error);
      return { success: false, error: error.message };
    }
  };

  // Add auto-save functionality
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (
        parsedText ||
        industryTrendsResponse ||
        workforceImpactResponse ||
        trainingResponse ||
        strategicSummaryResponse
      ) {
        saveAllFields();
      }
    }, 5000); // Auto-save every 5 seconds if there are changes

    return () => clearTimeout(autoSaveTimer);
  }, [
    parsedText,
    industryTrendsResponse,
    workforceImpactResponse,
    trainingResponse,
    strategicSummaryResponse,
  ]);

  // Update the handleSaveStrategicInput function
  const handleSaveStrategicInput = async () => {
    // Prevent multiple submissions
    if (isSavingStrategicInput) {
      return;
    }

    setIsSavingStrategicInput(true);
    try {
      const result = await saveAllFields();
      if (result.success) {
        // Reset the removed state when saving successfully
        const storageKey = `strategicInputForm_file_removed`;
        localStorage.removeItem(storageKey);
        toast.success("Changes saved successfully");
      } else {
        toast.error("Failed to save changes");
      }
    } catch (error) {
      console.error("Error saving strategic input:", error);
      toast.error("Failed to save strategic input: " + error.message);
    } finally {
      setIsSavingStrategicInput(false);
    }
  };

  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true);
    try {
      // Fetch the strategic summary prompt from prompt master
      const { data: prompts, error } = await supabase
        .from("prompt_master")
        .select("*")
        .eq("category", "strategicSummary")
        .eq("status", "true")
        .single();

      if (error) {
        throw new Error("Failed to fetch prompt: " + error.message);
      }

      if (!prompts) {
        throw new Error("No prompt found for strategic summary");
      }

      // Prepare the message with file content and current data
      // const messageWithContent = [
      //   // { role: "user", content: parsedText || "" },
      //   {
      //     role: "user",
      //     content: `${prompts.prompt}\n\nCurrent Data:\n\nIndustry Trends:\n${
      //       industryTrendsResponse || "No industry trends data available"
      //     }\n\nImpact on Workforce:\n${
      //       workforceImpactResponse || "No workforce impact data available"
      //     }\n\nTraining Response:\n${
      //       trainingResponse || "No training response data available"
      //     }\n\nGoals:\n${
      //       goals
      //         .map((goal) => `- ${goal.goal} (Timeline: ${goal.timeline})`)
      //         .join("\n") || "No goals data available"
      //     }`,
      //   },
      // ];

      const levelLabels = {
        essential: "Essential",
        intermediate: "Intermediate",
        expert: "Expert",
      };
      const messageWithContent = [
        {
          role: "user",
          content: `Prompt: ${
            prompts.prompt
          }\n\nCurrent Data:\n\nIndustry Trends:\n${
            industryTrendsResponse || "No industry trends data available"
          }\n\nImpact on Workforce:\n${
            workforceImpactResponse || "No workforce impact data available"
          }\n\nTraining Response:\n${
            trainingResponse || "No training response data available"
          }\n\nGoals:\n${
            goals
              .map((goal) => `- ${goal.goal} (Timeline: ${goal.timeline})`)
              .join("\n") || "No goals data available"
          }\n\nCritical Skills:\n${
            criticalSkills
              .map((skill) => `- ${skill.skill_name} (Description: ${skill.description || "N/A"}, Level: ${levelLabels[skill.level] || skill.level}, Importance: ${skill.importance})`)
              .join("\n") || "No critical skills data available"
          }\n\nDocument data: ${parsedText}`,
        },
      ];

      // Execute the chat prompt
      const result = await dispatch(
        executeChatPrompt({
          prompt: prompts.prompt,
          message: messageWithContent,
        })
      );

      if (result.payload?.data?.content) {
        setStrategicSummaryResponse(result.payload.data.content);
        toast.success("Strategic summary generated successfully!");
      } else {
        throw new Error("No response received from AI");
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      toast.error("Failed to generate strategic summary. Please try again.");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleExportToPDF = async () => {
    setIsExportingPDF(true);
    try {
      // Check if we have any data to export
      if (
        !parsedText &&
        !industryTrendsResponse &&
        !workforceImpactResponse &&
        !trainingResponse &&
        (!goals || goals.length === 0) &&
        (!criticalSkills || criticalSkills.length === 0)
      ) {
        toast.warning(
          "No data available to export. Please add some content first."
        );
        return;
      }

      // Save data independently for export (don't use handleSaveStrategicInput to avoid conflicts)
      const saveResult = await saveAllFields();
      if (!saveResult.success) {
        console.warn(
          "Failed to save before export, continuing with current data"
        );
      }

      // Fetch the strategic input analysis prompt using the existing function
      const promptData = await fetchPromptsByCategory(
        CHAT_CATEGORIES.STRATEGIC_INPUT_ANALYSIS.id
      );

      if (!promptData || promptData.length === 0) {
        console.warn(
          "No strategic analysis prompt found, using fallback prompt"
        );
        // Use a simple fallback prompt
        const fallbackPrompt =
          "Please provide a comprehensive analysis of the strategic input data including industry trends, workforce impact, training response, and goals.";
        promptData = [{ prompt: fallbackPrompt }];
      }

      // Use the first prompt (since allowMultiple is false for this category)
      let exportPrompt = promptData[0].prompt;

      // Only replace placeholders if they exist in the prompt
      if (exportPrompt.includes("<INDUSTRY_TRENDS>")) {
        exportPrompt = exportPrompt.replace(
          "<INDUSTRY_TRENDS>",
          industryTrendsResponse || "No industry trends data available"
        );
      }
      if (exportPrompt.includes("<WORKFORCE_IMPACT>")) {
        exportPrompt = exportPrompt.replace(
          "<WORKFORCE_IMPACT>",
          workforceImpactResponse || "No workforce impact data available"
        );
      }
      if (exportPrompt.includes("<TRAINING_RESPONSE>")) {
        exportPrompt = exportPrompt.replace(
          "<TRAINING_RESPONSE>",
          trainingResponse || "No training response data available"
        );
      }
      if (exportPrompt.includes("<GOALS>")) {
        exportPrompt = exportPrompt.replace(
          "<GOALS>",
          goals
            .map((goal) => `- ${goal.goal} (Timeline: ${goal.timeline})`)
            .join("\n") || "No goals data available"
        );
      }
      if (exportPrompt.includes("<CRITICAL_SKILLS>")) {
        const levelLabels = {
          essential: "Essential",
          intermediate: "Intermediate",
          expert: "Expert",
        };
        exportPrompt = exportPrompt.replace(
          "<CRITICAL_SKILLS>",
          criticalSkills
            .map((skill) => `- ${skill.skill_name} (Description: ${skill.description || "N/A"}, Level: ${levelLabels[skill.level] || skill.level}, Importance: ${skill.importance})`)
            .join("\n") || "No critical skills data available"
        );
      }

      const messageWithContent = [
        { role: "system", content: parsedText || "" },
        { role: "user", content: exportPrompt },
      ];

      const result = await dispatch(
        executeChatPrompt({
          prompt: exportPrompt,
          message: messageWithContent,
        })
      );

      if (!result.payload?.data?.content) {
        console.error("No content in AI response:", result);
        throw new Error("No response received from AI. Please try again.");
      }

      const aiGeneratedContent = result.payload.data.content;

      const doc = new jsPDF();
      const maxWidth = 170;
      let currentY = 20;
      const lineHeight = 7;
      const maxHeight = 250;

      // Helper function to add text with pagination
      const addText = (text, fontSize, yOffset = 0) => {
        doc.setFontSize(fontSize);
        const lines = doc.splitTextToSize(text, maxWidth);

        lines.forEach((line) => {
          if (currentY > maxHeight) {
            doc.addPage();
            currentY = 20;
          }
          doc.text(line, 20, currentY);
          currentY += lineHeight;
        });
        currentY += yOffset;
      };

      // Add document sections
      addText("Training Strategy Document", 20, 10);
      addText(`Generated on: ${new Date().toLocaleDateString()}`, 12, 8);
      addText("AI Generated Strategy Analysis", 16, 10);
      addText(aiGeneratedContent, 12, 12);
      addText("Industry Trends", 16, 10);
      addText(
        industryTrendsResponse || "No industry trends data available",
        12,
        8
      );
      addText("Impact on Workforce & Skills Development", 16, 10);
      addText(
        workforceImpactResponse || "No workforce impact data available",
        12,
        8
      );
      addText("Training & Development Response", 16, 10);
      addText(trainingResponse || "No training response data available", 12, 8);

      // --- Goals Section ---
      addText("Goals to Attain", 16, 2);
      if (goals && goals.length > 0) {
        autoTable(doc, {
          head: [["Sr. No", "Goal", "Benefits", "Timeline"]],
          body: goals.map((goal) => [
            goal.sr_number,
            goal.goal,
            goal.benefits,
            goal.timeline,
          ]),
          startY: currentY,
          margin: { top: 2, left: 15, right: 15 },
          styles: { cellPadding: 4, fontSize: 12, valign: "middle" },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 13,
            halign: "center",
            valign: "middle",
            lineWidth: 0.5,
          },
          alternateRowStyles: { fillColor: [245, 249, 255] },
          tableLineColor: [200, 200, 200],
          tableLineWidth: 0.2,
          didDrawPage: (data) => {
            currentY = data.cursor.y + 8;
          },
        });
      } else {
        addText("No goals defined", 12, 4);
      }

      // --- Critical Skills Section ---
      addText("Critical Skills", 16, 2);
      if (criticalSkills && criticalSkills.length > 0) {
        const levelLabels = {
          essential: "Essential",
          intermediate: "Intermediate",
          expert: "Expert",
        };
        autoTable(doc, {
          head: [["Sr. No", "Skill Name", "Description", "Level", "Importance"]],
          body: criticalSkills.map((skill, index) => [
            index + 1,
            skill.skill_name,
            skill.description || "N/A",
            levelLabels[skill.level] || skill.level,
            skill.importance,
          ]),
          startY: currentY,
          margin: { top: 2, left: 15, right: 15 },
          styles: { cellPadding: 4, fontSize: 12, valign: "middle" },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 13,
            halign: "center",
            valign: "middle",
            lineWidth: 0.5,
          },
          alternateRowStyles: { fillColor: [245, 249, 255] },
          tableLineColor: [200, 200, 200],
          tableLineWidth: 0.2,
          didDrawPage: (data) => {
            currentY = data.cursor.y + 8;
          },
        });
      } else {
        addText("No critical skills defined", 12, 4);
      }

      // --- Strategic Input Summary Section ---
      addText("Strategic Input Summary", 16, 10);
      addText(
        strategicSummaryResponse || "No strategic summary data available",
        12,
        8
      );

      try {
        doc.save("Training Strategy.pdf");
      } catch (pdfError) {
        console.error("Error saving PDF:", pdfError);
        toast.error("Failed to save PDF file. Please try again.");
      }
    } catch (error) {
      console.error("Error in handleExportToPDF:", error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    } finally {
      setIsExportingPDF(false);
    }
  };

  return (
    <div>
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold">
        Training Strategy
      </h2>
      <div className="mt-3 mb-4 flex items-center gap-3">
        <div className="flex items-center border border-blue-200 rounded-lg bg-white overflow-hidden flex-1">
          {/* Label inside container */}
          <label className="block font-medium text-gray-700 whitespace-nowrap px-4 py-3 flex-shrink-0">
            Strategic Input File:
          </label>
          {/* File name display area */}
          <div className="flex-1 px-4 py-3 min-h-[45px] 2xl:min-h-[50px] flex items-center">
            {uploaded && fileName ? (
              <p className="text-sm text-gray-700 font-medium truncate w-full text-center">{fileName}</p>
            ) : (
              <p className="text-sm text-gray-400 text-left">Upload your strategic input file here</p>
            )}
          </div>
          {/* Clear file icon inside container */}
          {uploaded && fileName && (
            <button
              onClick={clearFile}
              className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0 px-3"
              disabled={uploading}
              title="Remove file"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          )}
        </div>
        {/* Upload button outside container with space */}
        <button
          type="button"
          onClick={() => document.getElementById("fileInput").click()}
          disabled={uploading}
          className={`bg-blue-500 text-white px-4 py-2 h-[40px] hover:bg-blue-600 transition flex items-center gap-1 flex-shrink-0 rounded-lg text-sm leading-sm ${
            uploading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <ExportIcon className="!fill-white w-4 h-4" />
          <span className="whitespace-nowrap">{uploading ? "Uploading..." : "Upload File"}</span>
        </button>
        <input
          type="file"
          accept=".docx"
          onChange={handleFileUpload}
          className="hidden"
          id="fileInput"
          disabled={uploading}
        />
      </div>

      <div className="border border-solid border-blue-200 rounded-lg mt-4 overflow-hidden bg-white">
        <div className="bg-blue-100 p-2 px-3 flex items-center justify-between border-b border-blue-200">
          <p className="text-gray-700 font-medium 2xl:text-base 2xl:leading-base text-sm leading-sm">
            Strategic Input Text
          </p>
        </div>
        <div className="p-3">
          <CTextField
            value={parsedText}
            onChange={(e) => setParsedText(e.target.value)}
            placeholder="Extracted text from file"
            className="w-full"
            minRows={5}
            maxRows={10}
            multiline
          />
        </div>
      </div>

      <div className="border border-solid border-blue-200 rounded-lg mt-4 overflow-hidden bg-white">
        <div className="bg-blue-100 p-2 px-3 flex items-center justify-between border-b border-blue-200">
          <p className="text-gray-700 font-medium 2xl:text-base 2xl:leading-base text-sm leading-sm">
            Industry Trends
          </p>
          <CButton
            className=" gradient-btn text-white shadow-none !h-[40px] !text-sm !leading-sm"
            onClick={() => toggleChat(CHAT_CATEGORIES.INDUSTRY_TRENDS.id)}
          >
            iFundi AI
          </CButton>
        </div>
        <div className="p-3">
          <CTextField
            name="industryTrends"
            placeholder="AI Generated Response Extracted Here (Editable by SDF)"
            className="w-full critical-skill-name"
            minRows={3}
            maxRows={6}
            multiline
            value={industryTrendsResponse}
            onChange={(e) => setIndustryTrendsResponse(e.target.value)}
          />
        </div>
      </div>
      <div className="border border-solid border-blue-200 rounded-lg mt-4 overflow-hidden bg-white">
        <div className="bg-blue-100 p-2 px-3 flex items-center justify-between border-b border-blue-200">
          <p className="text-gray-700 font-medium 2xl:text-base 2xl:leading-base text-sm leading-sm">
            Impact of These Trends on Workforce & Skills Development
          </p>
          <CButton
            className=" gradient-btn text-white shadow-none !h-[40px] !text-sm !leading-sm"
            onClick={() => toggleChat(CHAT_CATEGORIES.WORKFORCE_IMPACT.id)}
          >
            iFundi AI
          </CButton>
        </div>
        <div className="p-3">
          <CTextField
            name="workforceImpact"
            placeholder="AI Generated Response Extracted Here (Editable by SDF)"
            className="w-full"
            minRows={3}
            maxRows={6}
            multiline
            value={workforceImpactResponse}
            onChange={(e) => setWorkforceImpactResponse(e.target.value)}
          />
        </div>
      </div>
      <div className="border border-solid border-blue-200 rounded-lg mt-4 overflow-hidden bg-white">
        <div className="bg-blue-100 p-2 px-3 flex items-center justify-between border-b border-blue-200">
          <p className="text-gray-700 font-medium 2xl:text-base 2xl:leading-base text-sm leading-sm">
            Training & Development Response
          </p>
          <CButton
            className=" gradient-btn text-white shadow-none !h-[40px] !text-sm !leading-sm"
            onClick={() => toggleChat(CHAT_CATEGORIES.TRAINING.id)}
          >
            iFundi AI
          </CButton>
        </div>
        <div className="p-3">
          <CTextField
            name="training"
            placeholder="AI Generated Response Extracted Here (Editable by SDF)"
            className="w-full"
            minRows={3}
            maxRows={6}
            multiline
            value={trainingResponse}
            onChange={(e) => setTrainingResponse(e.target.value)}
          />
        </div>
      </div>
      <div className="border border-solid border-blue-200 mt-4 rounded-lg overflow-hidden bg-white">
        <div className="flex p-2 px-3 justify-between items-center">
          <p className="2xl:text-xl 2xl:leading-xl font-semibold text-blue-500">
            Goal to Attain
          </p>
          <CButton
            className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700 !h-[40px] !text-sm !leading-sm !gap-1"
            onClick={handleAddGoal}
          >
            <AddIcon className="w-5" /> Add Goals
          </CButton>
        </div>
        <div>
          <CDataGrid
            idKey="id"
            rows={goals}
            columns={columns}
            loading={goalsLoading}
            initialPageSize={10}
          />
        </div>
      </div>

      <div className="border border-solid border-blue-200 mt-4 rounded-lg overflow-hidden bg-white">
        <div className="flex p-2 px-3 justify-between items-center">
          <p className="2xl:text-xl 2xl:leading-xl font-semibold text-blue-500">
            Critical Skills
          </p>
          <div className="flex items-center gap-2">
            <CButton
              className=" gradient-btn text-white shadow-none !h-[40px] !text-sm !leading-sm"
              onClick={() => toggleChat(CHAT_CATEGORIES.CRITICAL_SKILLS.id)}
            >
              iFundi AI
            </CButton>
            <CButton
              className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700 !h-[40px] !text-sm !leading-sm !gap-1"
              onClick={handleAddCriticalSkill}
            >
              <AddIcon className="w-5" /> Add Skills
            </CButton>
          </div>
        </div>
        <div>
          <CDataGrid
            idKey="id"
            rows={criticalSkills}
            columns={criticalSkillsColumns}
            loading={criticalSkillsLoading}
            initialPageSize={10}
          />
        </div>
      </div>

      <div className="border border-solid border-blue-200 rounded-lg mt-4 overflow-hidden bg-white">
        <div className="bg-blue-100 p-2 px-3 flex items-center justify-between border-b border-blue-200">
          <p className="text-gray-700 font-medium 2xl:text-base 2xl:leading-base text-sm leading-sm">
          Training Strategy
          </p>
          <CButton
            className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700 !h-[40px] !text-sm !leading-sm !gap-1"
            onClick={handleGenerateSummary}
            disabled={isGeneratingSummary}
          >
            <FileEdit className="w-5" />
            {isGeneratingSummary
              ? "Generating Summary..."
              : "Summarize Strategic Input"}
          </CButton>
          {/* <CButton
            className="gradient-btn text-white shadow-none !h-[40px] !text-sm !leading-sm"
            onClick={() => toggleChat(CHAT_CATEGORIES.STRATEGIC_SUMMARY.id)}
          >
            iFundi AI
          </CButton> */}
        </div>
        <div className="p-3">
          <CTextField
            name="strategicSummary"
            placeholder="AI Generated Response Extracted Here (Editable by SDF)"
            className="w-full"
            minRows={3}
            maxRows={6}
            multiline
            value={strategicSummaryResponse}
            onChange={(e) => setStrategicSummaryResponse(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-4 flex justify-start items-center gap-3">
        <CButton
          className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700"
          onClick={handleSaveStrategicInput}
          disabled={isSavingStrategicInput}
        >
          {/* <SaveIcon/> */}
          {isSavingStrategicInput ? "Saving..." : "Save"}
        </CButton>
        <CButton
          className="group text-blue-500 hover:text-white bg-white hover:bg-blue-500 border border-solid border-blue-500 hover:border-blue-500"
          onClick={handleExportToPDF}
          disabled={isExportingPDF}
        >
          {/* <ExportIcon className="!fill-blue-500 group-hover:!fill-white w-5 h-5"/> */}
          {isExportingPDF ? "Exporting..." : "Export"}
        </CButton>
      </div>
      <ChatModule
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onResponse={(response, section, updatedMessages) =>
          handleAIResponse(response, section, updatedMessages)
        }
        activeSection={activeSection}
        messages={chatHistories[activeSection] || []}
        onMessagesChange={(msgs) =>
          setChatHistories((prev) => ({ ...prev, [activeSection]: msgs }))
        }
        fileContent={parsedText}
        industryTrends={industryTrendsResponse}
        workforceImpact={workforceImpactResponse}
        trainingResponse={trainingResponse}
        goals={goals || []}
        criticalSkills={criticalSkills || []}
        onSkillsSaved={() => {
          // Refresh critical skills list after saving
          dispatch(fetchCriticalSkills());
        }}
      />

      {/* Add Goals Dialog */}
      <CDialog
        open={showAddGoalsDialog}
        onClose={handleCloseAddGoalsDialog}
        className="bg-[#95AEBB80]"
        maxWidth="sm"
        fullWidth
      >
        <div className="p-3">
          <div className="flex items-center justify-between border-b border-blue-200 pb-3">
            <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-blue-500">
              {editingGoal ? "Edit Goal" : "Add New Goal"}
            </p>
            <CButton
              className="!h-auto !p-0 text-blue-500 font-semibold !ml-auto !underline"
              onClick={handleCloseAddGoalsDialog}
            >
              <CloseIcon className="text-gray-700" />
            </CButton>
          </div>
          <CFormProvider
            methods={goalMethods}
            onSubmit={goalMethods.handleSubmit(handleSubmitGoal)}
          >
            <div className="mt-4 space-y-4">
              <CFormTextField
                name="goal"
                label="Goal"
                className="w-full"
                placeholder="Enter Goal"
              />
              <CFormTextField
                name="benefits"
                label="Benefits"
                className="w-full"
                placeholder="Enter Benefits"
              />
              <CFormTextField
                name="timeline"
                label="Timeline"
                className="w-full"
                placeholder="Enter Timeline"
              />
              <div className="flex justify-start gap-2 mt-4">
                <CButton
                  type="submit"
                  className="text-white bg-blue-500 hover:bg-blue-700"
                  disabled={isSavingGoal}
                >
                  {isSavingGoal ? "Saving..." : "Save"}
                </CButton>
                <CButton
                  type="button"
                  className="text-blue-500 hover:text-white bg-white hover:bg-blue-500 border border-solid border-blue-500"
                  onClick={handleCloseAddGoalsDialog}
                >
                  Cancel
                </CButton>
              </div>
            </div>
          </CFormProvider>
        </div>
      </CDialog>

      {/* Delete Confirmation Dialog */}
      {deleteDialogState.show && (
        <ConfirmationDialog
          open={deleteDialogState.show}
          onClose={() => setDeleteDialogState({ id: null, show: false })}
          onConfirm={handleConfirmArchive}
          title="Delete Goal"
          description="Are you sure you want to delete this goal?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmClass="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 border-blue-700 px-4 py-2 rounded-lg"
          cancelClass="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-blue-500 px-4 py-2 rounded-lg"
        />
      )}

      {/* Add/Edit Critical Skill Dialog */}
      <CDialog
        open={showAddCriticalSkillDialog}
        onClose={handleCloseAddCriticalSkillDialog}
        className="bg-[#95AEBB80]"
        maxWidth="sm"
        fullWidth
      >
        <div className="p-3">
          <div className="flex items-center justify-between border-b border-blue-200 pb-3">
            <p className="2xl:text-xl 2xl:leading-xl text-base leading-base font-semibold text-blue-500">
              {editingCriticalSkill
                ? "Edit Critical Skill"
                : "Add New Critical Skill"}
            </p>
            <CButton
              className="!h-auto !p-0 text-blue-500 font-semibold !ml-auto !underline"
              onClick={handleCloseAddCriticalSkillDialog}
            >
              <CloseIcon className="text-gray-700" />
            </CButton>
          </div>
          <CFormProvider
            methods={criticalSkillMethods}
            onSubmit={criticalSkillMethods.handleSubmit(handleSubmitCriticalSkill)}
          >
            <div className="mt-4 space-y-4">
              <CFormTextField
                name="skill_name"
                label="Skill Name"
                className="w-full p-5"
                placeholder="Enter Skill Name"
                helperText={`${SKILL_NAME_MIN_LENGTH}-${SKILL_NAME_MAX_LENGTH} characters required`}
                inputProps={{ maxLength: SKILL_NAME_MAX_LENGTH }}
                handleChange={(value) => {
                  // Enforce maximum length and validation
                  if (value && value.length > SKILL_NAME_MAX_LENGTH) {
                    criticalSkillMethods.setValue("skill_name", value.slice(0, SKILL_NAME_MAX_LENGTH));
                    return;
                  }
                  // Allow letters, numbers, spaces, hyphens, apostrophes, and other common characters
                  // Prevent pure numeric input (must contain at least one letter)
                  const stringPattern = /^[a-zA-Z0-9\s\-'.,()&]*$/;
                  if (value === "" || (stringPattern.test(value) && /[a-zA-Z]/.test(value))) {
                    criticalSkillMethods.setValue("skill_name", value);
                  }
                }}
              />
              <CFormTextField
                name="description"
                label="Description"
                className="w-full"
                minRows={3}
                maxRows={5}
                multiline
                helperText={`${DESCRIPTION_MIN_LENGTH}-${DESCRIPTION_MAX_LENGTH} characters required`}
                inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                handleChange={(value) => {
                  // Enforce maximum length
                  if (value && value.length > DESCRIPTION_MAX_LENGTH) {
                    criticalSkillMethods.setValue("description", value.slice(0, DESCRIPTION_MAX_LENGTH));
                  }
                }}
              />
              <CFormSelect
                name="level"
                label="Level"
                options={[
                  { id: "essential", value: "Essential" },
                  { id: "intermediate", value: "Intermediate" },
                  { id: "expert", value: "Expert" },
                ]}
                placeholder="Select Level"
                className="w-full"
              />
              <CFormSelect
                name="importance"
                label="Importance"
                options={[
                  { id: "1", value: "1" },
                  { id: "2", value: "2" },
                  { id: "3", value: "3" },
                  { id: "4", value: "4" },
                  { id: "5", value: "5" },
                ]}
                placeholder="Select Importance"
                className="w-full"
              />
              <div className="flex justify-start gap-2 mt-4">
                <CButton
                  type="submit"
                  className="text-white bg-blue-500 hover:bg-blue-700"
                  disabled={isSavingCriticalSkill}
                >
                  {isSavingCriticalSkill ? "Saving..." : "Save"}
                </CButton>
                <CButton
                  type="button"
                  className="text-blue-500 hover:text-white bg-white hover:bg-blue-500 border border-solid border-blue-500"
                  onClick={handleCloseAddCriticalSkillDialog}
                >
                  Cancel
                </CButton>
              </div>
            </div>
          </CFormProvider>
        </div>
      </CDialog>

      {/* Delete Critical Skill Confirmation Dialog */}
      {deleteCriticalSkillDialogState.show && (
        <ConfirmationDialog
          open={deleteCriticalSkillDialogState.show}
          onClose={() =>
            setDeleteCriticalSkillDialogState({ id: null, show: false })
          }
          onConfirm={handleConfirmArchiveCriticalSkill}
          title="Delete Critical Skill"
          description="Are you sure you want to delete this critical skill?"
          confirmText="Delete"
          cancelText="Cancel"
          confirmClass="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 border-blue-700 px-4 py-2 rounded-lg"
          cancelClass="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-blue-500 px-4 py-2 rounded-lg"
        />
      )}
    </div>
  );
}

export default StrategicInputForm;
